<script setup lang="ts">
  import { computed, toRef } from 'vue';
  import { useRouter } from 'vue-router';
  import { useToast } from 'vue-toastification';
  import { useHomologations } from '@/features/Document';
  import Eye from '@/shared/assets/icons/EyeGray.svg';
  import BadgeCheck from '@/shared/assets/icons/BadgeCheck.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import {
    buildHomologationDetailPath,
    DocumentRevisionStatus,
    DocumentRevisionStatusLabels,
    DocumentRevisionStatusShortLabels,
    DocumentType,
    formatCivilDateToPtBR,
    formatInstantToPtBR,
  } from '@/shared/lib';
  import { formatDocumentPrefix, getDocumentRevisionStatusClasses } from '@/shared/lib/utils';
  import {
    isHomologatableStatus,
    useHomologationRowPermissions,
    useRecordRowPermissions,
  } from '../model';
  import type { Homologation } from '../model';
  import { IconImage, Tooltip } from '@/shared/ui';

  const router = useRouter();
  const toast = useToast();
  const { viewHomologationDocument: downloadDocument } = useHomologations();
  const emit = defineEmits(['delete']);

  const props = defineProps<{
    revision: Homologation;
    documentType?: DocumentType;
  }>();

  const isRecord = computed(() => props.documentType === DocumentType.REGISTRATION);

  const getReviewPhaseName = (reviewPhase: string) => {
    const status = reviewPhase as DocumentRevisionStatus;

    if (DocumentRevisionStatusShortLabels[status]) {
      return DocumentRevisionStatusShortLabels[status];
    }

    return DocumentRevisionStatusLabels[status] || reviewPhase;
  };

  const getCurrentRevisionStatus = (revision: Homologation): DocumentRevisionStatus | null =>
    revision.status || null;

  const resolveHomologateTarget = (revision: Homologation) => {
    if (!revision.id) return null;

    const currentStatus = getCurrentRevisionStatus(revision);
    if (!currentStatus || !isHomologatableStatus(currentStatus)) {
      return null;
    }

    // O id sozinho nao diz de qual tabela o item veio: cada natureza tem a sua rota de detalhe.
    const basePath =
      currentStatus === DocumentRevisionStatus.PENDING_APPROVAL ? '/aprovacao' : '/verificacao';

    return buildHomologationDetailPath(basePath, revision.id, props.documentType);
  };

  const resolveRecordViewTarget = (revision: Homologation) => {
    const currentStatus = getCurrentRevisionStatus(revision);

    if (!revision.id || !currentStatus) {
      return null;
    }

    return {
      path: `/homologacao/registro/${revision.id}`,
      query: { status: currentStatus },
    };
  };

  const viewDocument = (revision: Homologation) => {
    const target = resolveHomologateTarget(revision);

    if (!target) {
      toast.error('Não foi possível abrir a tela de homologação para este documento.');
      return;
    }

    router.push(target);
  };

  const deleteDocument = (revision: Homologation) => {
    if (!revision.id) {
      toast.error('Não foi possível excluir este documento.');
      return;
    }

    emit('delete', revision.id);
  };

  const viewHomologationRecord = async (revision: Homologation) => {
    const target = resolveRecordViewTarget(revision);

    if (!target) {
      toast.error('Não foi possível abrir a visualização deste registro.');
      return;
    }

    await router.push(target);
  };

  const viewHomologationDocument = async (revision: Homologation) => {
    if (isRecord.value) {
      await viewHomologationRecord(revision);
      return;
    }

    const documentId = revision.documentId ?? revision.document?.id;

    if (!documentId) {
      toast.error('Não foi possível visualizar este documento.');
      return;
    }

    await downloadDocument(documentId.toString(), revision.id);
  };

  const approvedByName = computed(() => props.revision.approvedBy?.name ?? '-');

  const revisionRef = toRef(props, 'revision');
  const documentPermissions = useHomologationRowPermissions(revisionRef);
  const recordPermissions = useRecordRowPermissions(revisionRef);

  const canView = computed(() =>
    isRecord.value ? recordPermissions.canView.value : documentPermissions.canViewHomologation.value
  );
  const canHomologate = computed(() =>
    isRecord.value ? recordPermissions.canHomologate.value : documentPermissions.canHomologate.value
  );
  const canRemove = computed(() =>
    isRecord.value ? recordPermissions.canDelete.value : documentPermissions.canDelete.value
  );
  const isHomologatable = documentPermissions.isHomologatable;

  const viewLabel = computed(() => (isRecord.value ? 'Visualizar registro' : 'Visualizar'));
  const viewAriaLabel = computed(() =>
    isRecord.value ? 'Visualizar registro' : 'Visualizar documento'
  );
  const removeAriaLabel = computed(() =>
    isRecord.value ? 'Remover registro' : 'Deletar documento'
  );
</script>

<template>
  <tr class="table-row-hover border-t border-gray-02 transition-colors duration-300 ease-out">
    <td class="pl-6 py-4.5 pr-0">
      <div class="text-sm text-gray-07">
        {{ formatDocumentPrefix(revision.document?.prefix) }}
      </div>
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ revision.revision || '-' }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatInstantToPtBR(revision.createdAt) }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ revision.document?.createdBy?.name || '-' }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatInstantToPtBR(revision.approvedAt) }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ approvedByName }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formatCivilDateToPtBR(revision.effectiveDate) }}
    </td>
    <td class="pl-6 py-4.5">
      <div class="flex items-center">
        <span
          v-if="getCurrentRevisionStatus(revision)"
          :class="getDocumentRevisionStatusClasses(getCurrentRevisionStatus(revision)!)"
          class="text-center"
        >
          {{ getReviewPhaseName(getCurrentRevisionStatus(revision)!) }}
        </span>
        <span
          v-else
          class="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center bg-gray-100 text-gray-600"
        >
          N/A
        </span>
      </div>
    </td>
    <td class="px-6 py-4.5 text-sm text-gray-07 text-left">
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <Tooltip
          v-if="canView"
          :content="viewLabel"
          class="flex items-center"
        >
          <button
            type="button"
            :aria-label="viewAriaLabel"
            class="flex items-center justify-center"
            @click="viewHomologationDocument(revision)"
          >
            <IconImage
              :src="Eye"
              alt="Ícone de Visualização"
            />
          </button>
        </Tooltip>
        <Tooltip
          v-if="isHomologatable && canHomologate"
          content="Homologar"
          class="flex items-center"
        >
          <button
            type="button"
            aria-label="Homologar documento"
            class="flex items-center justify-center"
            @click="viewDocument(revision)"
          >
            <IconImage
              :src="BadgeCheck"
              alt="Ícone de Homologação"
            />
          </button>
        </Tooltip>
        <Tooltip
          v-if="canRemove"
          content="Deletar"
          class="flex items-center"
        >
          <button
            type="button"
            :aria-label="removeAriaLabel"
            class="flex items-center justify-center"
            @click="deleteDocument(revision)"
          >
            <IconImage
              :src="Trash"
              alt="Ícone de Deleção"
            />
          </button>
        </Tooltip>
      </div>
    </td>
  </tr>
</template>
